// native
import { Injectable } from '@angular/core';

// models
import { DeviceStepResult, NetworkStepResult, PatientStepResult } from './home-management.types';

export interface HomeManagementWizardState {
  currentStep: number;
  patient: PatientStepResult | null;
  device: DeviceStepResult | null;
  network: NetworkStepResult | null;
}

const STORAGE_KEY = 'home_management_wizard_state';
// Resume the wizard only if the user comes back within this window.
const TTL_MS = 15 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class HomeManagementStateService {

  // Persisted in localStorage so progress survives navigating away and back
  // within the same browser. A different browser has no entry (starts over),
  // and entries older than the TTL are discarded (starts over).
  //
  // The persisted network step includes the Wi-Fi password so a resumed
  // session keeps working. Its lifetime is bounded: the wizard clears the
  // state on successful enrollment, logout wipes localStorage entirely, and
  // stale entries expire via the TTL below.
  save(state: HomeManagementWizardState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), state }));
    } catch {
      // Ignore storage quota / serialization errors - persistence is best-effort.
    }
  }

  load(): HomeManagementWizardState | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw)
      return null;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.savedAt || (Date.now() - parsed.savedAt) > TTL_MS) {
        this.clear();
        return null;
      }
      return parsed.state as HomeManagementWizardState;
    } catch {
      this.clear();
      return null;
    }
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}
